<template>
    <div class="customization-style-transfer-upload-image">
        <div class="customization-upload-image-upload-title mt-sm">{{ title ? title : 'Upload Photo *' }}</div>
        <div class="customization-style-transfer-upload-image-button-wrap">
            <CustomFileUpload></CustomFileUpload>
            <span class="tooltip-image-processing" v-show="loading" style="transform: translateY(3px);">
                {{ imageProcessingText }}
                {{ percentProcessed ? `${percentProcessed}%` : '' }}
            </span>
            <img v-show="getLinkImagePreview() && !loading" class="style-transfer-image-preview" :src="getLinkImagePreview() || ''" alt=""/>
            <span class="customization-style-transfer-upload-image-message" v-show="isShowMessage"
                :class="messageClasses">
                {{ messageUpload }}
            </span>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, h, onMounted, ref, watch } from 'vue'
import FileUpload from '@/ui-kits/FileSelect.vue'
import Logger from '@/services/log'
import useCustomizationStore from '@/store/customization'
import {
    EVENT_CUSTOM_UPLOAD_STYLE_TRANSFER_IMAGE,
    SIB_FILE_SELECT_WRAPPER_CLASS,
    QUERY_STRING_NO_CACHE,
} from '@/utils'
import EventBus from '@/services/eventbus'
import { useProductCustomization } from './composables/ProductCustomization'
import { useRoute } from 'vue-router'
import { CustomizationTemplate } from '@/types'

const route = useRoute()
const customizationStore = useCustomizationStore()
const {
    startUpload,
    finishUpload,
    percentProcessed,
} = useProductCustomization()

const props = withDefaults(
    defineProps<{
        title?: string
        id: string
        selectImageButtonCustomily: Element
        customizationTemplate: CustomizationTemplate
        photoNumber: number,
        listImageUploadSelected: any
    }>(),
    {}
)

const CUSTOMIZATION_UPLOAD_IMAGE_WRAPPER_CLASS = 'customization-style-transfer-upload-image'

const messageUpload = computed(() =>
    isSuccess.value ? successfullMessage : errorMessage.value
)
const messageClasses = computed(() =>
    isSuccess.value
        ? 'text-primary ml-xs'
        : `text-secondary ${CUSTOMIZATION_UPLOAD_IMAGE_WRAPPER_CLASS}-message--error`
)

let loading = ref(false)
let isSuccess = ref(false)
let isShowMessage = ref(false)
const imageProcessingText = 'Processing'
const successfullMessage = 'Successful!'
let errorMessage = ref('Error!')
let styleTransferImageUploadedLink = ref('')

onMounted(async () => {
    watch(
        () => loading.value,
        (value) => {
            handleClassListUploadButtons(value)
            if (value) startUpload()
            else finishUpload()
        }
    )
})

const CustomFileUpload = () => {
    let tag: any = FileUpload
    const propsCustomFileUpload: {
        [key: string]: any
    } = {}

    propsCustomFileUpload.id = props.id
    propsCustomFileUpload.loading = loading.value
    propsCustomFileUpload.onChange = onChange

    async function onChange(event: Event) {
        const input = event.target as HTMLInputElement
        if (!input.files) return
        const file = input.files[0]
        if (!file) return

        loading.value = true
        isSuccess.value = false
        isShowMessage.value = false

        const payload = {
            file,
            id: props.customizationTemplate.id,
            isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
        }

        customizationStore
            .uploadImageTransferDefault(payload)
            .then(async (response) => {
                if (response.success === false) {
                    throw response
                }
                const link = response
                styleTransferImageUploadedLink.value = link

                isSuccess.value = true
            })
            .catch((err) => {
                errorMessage.value = err.message ?? errorMessage.value
                isSuccess.value = false
                Logger.log('Error when face swap', err)
            })
            .finally(() => {
                if (isSuccess.value) {
                    EventBus.trigger(EVENT_CUSTOM_UPLOAD_STYLE_TRANSFER_IMAGE, { link: styleTransferImageUploadedLink.value , photoNumber: props.photoNumber})
                    customilyUpLoadingHandler()
                } else {
                    loading.value = false
                    isShowMessage.value = true
                }
            })
        input.value = ''
    }

    return h(tag, propsCustomFileUpload)
}

function customilyUpLoadingHandler() {
    const timeout = setTimeout(() => {
        const isCustomilyUpLoading =
            props.selectImageButtonCustomily?.classList.contains('disabled')
        if (isCustomilyUpLoading) {
            customilyUpLoadingHandler()
        } else {
            loading.value = false
            isShowMessage.value = true
            clearTimeout(timeout)
        }
    }, 200)
}

function handleClassListUploadButtons(loading: boolean) {
    const uploadFileButtons = document.querySelectorAll(
        `.${CUSTOMIZATION_UPLOAD_IMAGE_WRAPPER_CLASS} .${SIB_FILE_SELECT_WRAPPER_CLASS}`
    )

    // cancel event select file while processing face swap image
    uploadFileButtons.forEach((button) => {
        if (loading) {
            button.classList.add(`${SIB_FILE_SELECT_WRAPPER_CLASS}--loading`)
        } else {
            button.classList.remove(`${SIB_FILE_SELECT_WRAPPER_CLASS}--loading`)
        }
    })
}

function getLinkImagePreview(): string {
    if (props.listImageUploadSelected?.length) {
      const item = props.listImageUploadSelected?.find((item: any) => Number(item.photoNumber) == Number(props.photoNumber))
        if (item) {
            return item.link
        }
    }
    return ''
}
</script>

<style lang="scss">
.customization-style-transfer-upload-image-button-wrap {
    display: flex;
    align-items: center;
    .sib-file-select-wrapper {
        width: auto;
    }

    .style-transfer-image-preview {
        margin-left: 8px;
        width: 42px;
        height: 42px;
        object-fit: contain;
        border: 1px solid #d5d5d5;
    }
}
</style>
